import { AssessmentComponentTypeEnum } from '../../../constants.new';

export interface ReportingParcelTile extends Core.ParcelTileDTO {
    isExternalReportingParcel?: boolean;
    isReportingParcel?: boolean;
    capValue?: number;
}

export interface ParcelListControlRow extends ReportingParcelTile {
    isTotalRow: boolean;
    returnCount: number;
    totalFMV: number;
    totalTaxes: number;
    totalComponents: { [componentName: string]: number };
    siteID?: number;
    companyID?: number;
    originalIsReportingParcel?: boolean;
    originalConsolidatingTypeId?: number;
    originalConsolidatedParcelId?: number;
    parcelsHaveTaxable?: boolean;
    parcelsHaveCap?: boolean;
}

export class ParcelList {
    constructor(
        private readonly _incomingParcels: Core.ParcelTileDTO[]) {
        this._initialize();
    }

    private _tiles: ParcelListControlRow[] = [];
    private _parcels: ParcelListControlRow[] = [];

    private _parcelsRepository: ParcelListControlRow[] = [];

    get tiles(): ParcelListControlRow[] {
        return this._tiles;
    }

    get parcels(): ParcelListControlRow[] {
        return this._parcels;
    }

    filterInactive(show: boolean): void {
        if (show) {
            const filtered = this._parcelsRepository.filter((p) => p.activityStatusID !== 0);
            this._tiles = filtered;
            this._parcels = [...filtered, this._getTotalsRow(filtered)];
        } else {
            this._tiles = [...this._parcelsRepository];
            this._parcels = [...this._parcelsRepository, this._getTotalsRow(this._parcelsRepository)];
        }
    }

    taxableValueLabel(index: number): string {
        const name = index === 0 ? 'Taxable' : `Taxable${index + 1}`;
        const description = this._sharedTaxableValueDescription(index);

        return description ? `${name} (${description})` : name;
    }

    private _sharedTaxableValueDescription(index: number): string {
        const descriptions = this._tiles
            .filter((parcel) => !!parcel.taxableValues?.[index])
            .map((parcel) => (parcel.taxableValues[index].description ?? '').trim());

        if (!descriptions.length) {
            return '';
        }

        const first = descriptions[0];
        const isShared = descriptions.every((description) => description.toLowerCase() === first.toLowerCase());

        return isShared ? first : '';
    }

    private _initialize() {
        const parcelsHaveTaxable = this._incomingParcels.some(parcel => parcel.taxableValues && parcel.taxableValues.length > 0);
        const parcelsHaveCap = this._incomingParcels.some(parcel => parcel.components.find(x => x.assessmentComponentTypeID === AssessmentComponentTypeEnum.Cap));
        const parcels = this._incomingParcels.map(parcel => {
            const nonCapComponents = parcel.components?.filter(x => x.assessmentComponentTypeID !== AssessmentComponentTypeEnum.Cap) || [];
            const capComponent = parcel.components?.find(x => x.assessmentComponentTypeID === AssessmentComponentTypeEnum.Cap);
            const taxableValues = parcel.taxableValues?.reduce((acc, x, i: number) => {
                const tv = 'taxable' + (i > 0 ? i + 1 : '');
                acc[tv] = x.fairMarketValue;
                return acc;
            }, {}) || {};

            return {
                ...parcel,
                ...taxableValues,
                ...{
                    isTotalRow: false,
                    returnCount: 1,
                    originalConsolidatingTypeId: parcel.consolidatingTypeId,
                    originalConsolidatedParcelId: parcel.consolidatedParcelId,
                    components: nonCapComponents.sort((a, b) => {
                        if (a.assessmentComponentTypeID !== b.assessmentComponentTypeID) {
                            return a.assessmentComponentTypeID - b.assessmentComponentTypeID;
                        }
                        return a.sequence - b.sequence;
                    }),
                    capValue: capComponent ? capComponent.fairMarketValue : null,
                    parcelsHaveTaxable,
                    parcelsHaveCap
                }
            } as ParcelListControlRow;
        }).sort((i1, i2) => {
            return i1.acctNum.localeCompare(i2.acctNum);
        });

        this._parcelsRepository = parcels;
        this._tiles = parcels;
        this._parcels = [
            ...parcels,
            this._getTotalsRow(parcels)
        ];
    }

    private _getTotalsRow(parcels: ParcelListControlRow[]): ParcelListControlRow {
        const totalComponents = {};
        parcels.forEach(p => {
            if (p.components && p.components.length) {
                p.components.forEach(c => {
                    if (!totalComponents[c.componentName]) {
                        totalComponents[c.componentName] = 0;
                    }
                    totalComponents[c.componentName] += c.fairMarketValue;
                });
            }
        });
        return {
            isFmvInclude: true,
            isTotalRow: true,
            returnCount: parcels.length,
            totalFMV: parcels.reduce(
                (acc: number, x: ParcelListControlRow) => (x.totalFMV && x.isFmvInclude) ? (acc += x.totalFMV) : acc,
                0),
            totalTaxes: parcels.reduce(
                (acc: number, x: ParcelListControlRow) => (x.totalTaxes) ? (acc += x.totalTaxes) : acc,
                0),
            totalComponents
        } as ParcelListControlRow;
    }
}
